import React, { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Modal from '../../../shared/components/ui/Modal';
import { convertLend, returnLend } from '../../services/api/implementation/impl';
import { useProjectWarehouses } from '../../hooks/useInventorySelection';
import { errorMessage, roundQty } from '../../utils/stockTransfers';

const QTY_PATTERN = /^\d*\.?\d{0,3}$/;
// The shared Modal takes a % width; go near full width on phones.
const modalWidth = (desktop) =>
  typeof window !== 'undefined' && window.innerWidth < 768 ? 96 : desktop;

/**
 * Borrower sends back a lend: all of it, or part of each line, from any of
 * its warehouses (default: the one the lend arrived in).
 */
export const ReturnLendModal = ({ lend, projectId, onClose, onDone }) => {
  const { active } = useProjectWarehouses(projectId);
  const openLines = useMemo(
    () => (lend.items || []).filter((i) => Number(i.outstanding) > 0),
    [lend]
  );
  const [warehouseId, setWarehouseId] = useState(lend.to_warehouse?.id || '');
  const [note, setNote] = useState('');
  const [quantities, setQuantities] = useState(() =>
    Object.fromEntries(openLines.map((i) => [i.id, String(roundQty(i.outstanding))]))
  );

  // The lend's own warehouse stays selectable even if the list hasn't loaded.
  const warehouses = useMemo(() => {
    const list = [...(active || [])];
    if (lend.to_warehouse?.id && !list.some((w) => Number(w.id) === Number(lend.to_warehouse.id))) {
      list.unshift({ id: lend.to_warehouse.id, name: lend.to_warehouse.name });
    }
    return list;
  }, [active, lend]);

  const mutation = useMutation({
    mutationFn: ({ all }) => {
      if (!warehouseId) throw new Error('Pick the warehouse to return from');
      const payload = {
        from_warehouse_id: Number(warehouseId),
        ...(note.trim() && { note: note.trim() })
      };
      if (all) return returnLend(lend.id, payload);
      const items = [];
      for (const line of openLines) {
        const text = String(quantities[line.id] ?? '').trim();
        if (text === '' || Number(text) === 0) continue;
        const qty = Number(text);
        if (!QTY_PATTERN.test(text) || !(qty > 0)) {
          throw new Error(`Quantity for ${line.item_name} must be more than 0, up to 3 decimals`);
        }
        if (qty > Number(line.outstanding)) {
          throw new Error(`Only ${roundQty(line.outstanding)} of ${line.item_name} is still out`);
        }
        items.push({ transfer_item_id: line.id, quantity: qty });
      }
      if (!items.length) throw new Error('Enter a quantity for at least one line');
      return returnLend(lend.id, { ...payload, items });
    },
    onSuccess: (_data, { all }) => {
      toast.success(
        all ? `Lend #${lend.id} returned in full` : `Return saved for lend #${lend.id}`
      );
      onDone?.();
      onClose();
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not return the lend'))
  });

  return (
    <Modal name={`Return lend #${lend.id}`} width={modalWidth(55)} onClose={onClose}>
      <p className="text-sm text-gray-600">
        Goes back to{' '}
        <span className="font-semibold">{lend.from_warehouse?.name || 'the lender'}</span>
        {lend.from_project_name ? ` (${lend.from_project_name})` : ''}.
      </p>

      <label className="mt-4 block text-sm">
        <span className="mb-1 block text-gray-600">Return from</span>
        <select
          value={warehouseId}
          onChange={(e) => setWarehouseId(e.target.value ? Number(e.target.value) : '')}
          className="h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800 sm:w-72">
          <option value="">Select warehouse</option>
          {warehouses.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
              {w.is_default ? ' (default)' : ''}
              {Number(w.id) === Number(lend.to_warehouse?.id) ? ' (received here)' : ''}
            </option>
          ))}
        </select>
      </label>

      <div className="mt-4 overflow-x-auto rounded-lg border border-gray-100">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-[#F4F7F8] text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Item</th>
              <th className="px-3 py-2">Still out</th>
              <th className="px-3 py-2">Return qty</th>
            </tr>
          </thead>
          <tbody>
            {openLines.map((line) => {
              const value = quantities[line.id] ?? '';
              const tooMuch = value !== '' && Number(value) > Number(line.outstanding);
              return (
                <tr key={line.id} className="border-t border-gray-100">
                  <td className="px-3 py-2">
                    <p className="font-medium text-gray-900">{line.item_name}</p>
                    <p className="text-xs text-gray-500">{line.unit || ''}</p>
                  </td>
                  <td className="px-3 py-2">{roundQty(line.outstanding)}</td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={0}
                      step="0.001"
                      max={line.outstanding}
                      value={value}
                      onChange={(e) =>
                        setQuantities((prev) => ({ ...prev, [line.id]: e.target.value }))
                      }
                      className={`h-9 w-28 rounded-lg border px-2 outline-none focus:border-[#2A565E] ${
                        tooMuch ? 'border-red-500' : 'border-gray-200'
                      }`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <label className="mt-4 block text-sm">
        <span className="mb-1 block text-gray-600">Note (optional)</span>
        <input
          maxLength={255}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Pipes back after slab 4"
          className="h-10 w-full rounded-lg border border-gray-200 px-3 outline-none focus:border-[#2A565E]"
        />
      </label>

      <div className="mt-4 flex flex-wrap justify-end gap-3 border-t pt-4">
        <button
          type="button"
          className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700"
          onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          disabled={mutation.isPending}
          className="rounded-md border border-[#2A565E] px-4 py-2 text-sm font-semibold text-[#2A565E] disabled:opacity-60"
          onClick={() => mutation.mutate({ all: true })}>
          Return all
        </button>
        <button
          type="button"
          disabled={mutation.isPending}
          className="rounded-md bg-[#2A565E] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          onClick={() => mutation.mutate({ all: false })}>
          {mutation.isPending ? 'Returning…' : 'Return these quantities'}
        </button>
      </div>
    </Modal>
  );
};

/** Lender lets the borrower keep what is still out; the lend becomes a transfer. */
export const ConvertLendModal = ({ lend, onClose, onDone }) => {
  const mutation = useMutation({
    mutationFn: () => convertLend(lend.id),
    onSuccess: () => {
      toast.success(`Lend #${lend.id} converted to a transfer`);
      onDone?.();
      onClose();
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not convert the lend'))
  });
  const openLines = (lend.items || []).filter((i) => Number(i.outstanding) > 0);

  return (
    <Modal
      name={`Convert lend #${lend.id} to a transfer?`}
      width={modalWidth(40)}
      onClose={onClose}>
      <p className="text-sm text-gray-700">
        {lend.to_project_name || 'The borrowing project'} keeps what is still out. Nothing more is
        owed back, and this can&apos;t be undone.
      </p>
      {openLines.length > 0 && (
        <ul className="mt-3 list-disc pl-5 text-sm text-gray-700">
          {openLines.map((line) => (
            <li key={line.id}>
              {roundQty(line.outstanding)} {line.unit || ''} {line.item_name}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-4 flex justify-end gap-3 border-t pt-4">
        <button
          type="button"
          className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700"
          onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          disabled={mutation.isPending}
          className="rounded-md bg-[#2A565E] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Converting…' : 'Convert to transfer'}
        </button>
      </div>
    </Modal>
  );
};
